import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSidebar } from "../../context/sidebar";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useMemo, useEffect } from "react";
import useUserRoleStore from "../../store/user-role";
import roles, { isAdmin, isStaffMember } from "../../utils/roles";
import { useLogout } from "../../utils/logout";
import {
  Home,
  Clipboard,
  Users,
  Package,
  MessageSquare,
  DollarSign,
  LogOut,
  User,
} from "lucide-react";

const Sidebar = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const { role, setRole } = useUserRoleStore();

  const { isOpen, setIsOpen } = useSidebar();

  const navigate = useNavigate();
  const logOut = useLogout();
  const location = useLocation();

  const menus = useMemo(
    () => [
      {
        name: "Dashboard",
        link: "/dashboard",
        icon: <Clipboard />,
        // The finance dashboard. Staff do not see revenue.
        render: isAdmin(role),
      },
      {
        name: "Equipment",
        icon: <Clipboard />,
        subMenus: [
          { name: "Meters", link: "/meters" },
          { name: "Grids", link: "/grids" },
        ],
        render: isStaffMember(role),
      },
      {
        name: "Clients",
        icon: <Users />,
        subMenus: [{ name: "Dashboard", link: "/clients-dashboard" }],
        render: isStaffMember(role),
      },
      {
        name: "Packages",
        icon: <Package />,
        subMenus: [{ name: "packages", link: "/packages" }],
        render: isStaffMember(role),
      },
      {
        name: "Communication",
        icon: <MessageSquare />,
        subMenus: [
          { name: "Dashboard", link: "/communication/dashboard" },
          { name: "Telemetry", link: "/communication/telemetry" },
        ],
        render: isStaffMember(role),
      },
      {
        name: "Appliances",
        icon: <Home />,
        link: "/appliances",
        render: isStaffMember(role),
      },
      {
        name: "Revenue",
        icon: <DollarSign />,
        subMenus: [
          { name: "Recent Transactions", link: "/revenue/recent-transactions" },
          { name: "Non- Paying Clients", link: "/revenue/none-paying-clients" },
        ],
        render: isAdmin(role),
      },
      {
        name: "Users",
        icon: <Users />,
        link: "/users",
        render: isAdmin(role),
      },
      {
        name: "My Power",
        icon: <User />,
        link: "/my-power",
        render: role === roles.Client,
      },
      {
        name: "My Profile",
        icon: <User />,
        link: "/my-profile",
        render: role === roles.Client,
      },
      {
        name: "My History",
        icon: <Clipboard />,
        link: "/my-history",
        render: role === roles.Client,
      },
      {
        name: "Top Up",
        icon: <DollarSign />,
        link: "/top-up",
        render: role === roles.Client,
      },
    ],
    [role]
  );

  const [activeLink, setActiveLink] = useState(null);

  useEffect(() => {
    const currentPath = location.pathname;

    const matchedMenu = menus.find((menu) => {
      if (menu.link) {
        return currentPath.startsWith(menu.link);
      } else if (menu.subMenus) {
        return menu.subMenus.some((subMenu) =>
          currentPath.startsWith(subMenu.link)
        );
      }
      return false;
    });

    if (matchedMenu) {
      setActiveLink(matchedMenu.link);
    } else {
      setActiveLink(null);
    }
  }, [location.pathname, menus]);

  const handleMenuClick = (menuIndex) => {
    setActiveMenu(activeMenu === menuIndex ? null : menuIndex);
  };

  const onSidebarClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const hasSubMenu = (item) => {
    return item.subMenus?.length;
  };

  const onNavigate = (link) => {
    navigate(link);
    setIsOpen(false);
  };

  // Shared with the 401 interceptor and the GraphQL error link, so a client is
  // returned to the landing page however they end up logged out. This used to
  // navigate to /login itself, which sent clients to the admin form.
  const onLogout = () => {
    localStorage.removeItem("fcm_token");
    setRole(undefined);
    setIsOpen(false);
    logOut();
  };

  return (
    <div className={`sidebar ${isOpen ? "sidebar--open" : ""}`}>
      <div className="sidebar__logo-wrapper">
        <img
          className="sidebar__logo"
          src={"/images/zonkelogo.jpg"}
          alt="logo"
        />
      </div>
      <div className="sidebar__menu-icon-wrapper" onClick={onSidebarClick}>
        {isOpen ? (
          <img
            className="sidebar__menu-icon sidebar__menu-icon--close"
            src={"/images/cancel-close.svg"}
            alt=""
          />
        ) : (
          <img
            className="sidebar__menu-icon sidebar__menu-icon--open"
            src={"/images/menu.png"}
            alt=""
          />
        )}
      </div>
      {menus.map(
        (menu, index) =>
          menu.render && (
            <div key={index}>
              {hasSubMenu(menu) ? (
                <>
                  <div
                    className={`sidebar__menu ${
                      activeMenu === index ? "sidebar__menu--active" : ""
                    }`}
                    onClick={() => handleMenuClick(index)}
                  >
                    <div className="sidebar__menu-icon-wrapper">
                      {menu.icon}
                    </div>
                    {menu.name}
                    <div className="sidebar__chevron-wrapper">
                      <img
                        className={`sidebar__chevron ${
                          activeMenu === index ||
                          activeLink?.startsWith(menu.link)
                            ? "sidebar__chevron--active"
                            : ""
                        }`}
                        src={"/images/chevron-right.svg"}
                        alt="chevron-right"
                      />
                    </div>
                  </div>
                  <div
                    className={`sidebar__sub-menu ${
                      activeMenu === index && "sidebar__sub-menu--open"
                    }`}
                  >
                    {menu.subMenus.map((subMenu, subIndex) => (
                      <NavLink
                        key={subIndex}
                        to={subMenu.link}
                        className="sidebar__sub-menu-item"
                        onClick={() => setIsOpen(false)} // Close the sidebar when a submenu item is clicked
                      >
                        {subMenu.name}
                      </NavLink>
                    ))}
                  </div>
                </>
              ) : (
                <div
                  className={`sidebar__menu ${
                    activeMenu === index || activeLink?.startsWith(menu.link)
                      ? "sidebar__menu--active"
                      : ""
                  }`}
                  onClick={() => onNavigate(menu.link)}
                >
                  <div className="sidebar__menu-icon-wrapper">{menu.icon}</div>
                  {menu.name}
                </div>
              )}
            </div>
          )
      )}

      <div className="sidebar__menu sidebar__logout" onClick={onLogout}>
        <div className="sidebar__menu-icon-wrapper">
          <LogOut />
        </div>
        Logout
      </div>
    </div>
  );
};

export default Sidebar;
